import { useState } from "react";
import type { TransportTrackMonitorMapVo, TransportTrackMonitorVo } from "@/api/transportTrackMonitorTypes";
import { ZhihaoMap } from "@/components/media";
import { getCaptureCategoryOption } from "./presentation";

interface CaptureMapProps {
    records: TransportTrackMonitorVo[];
    map?: TransportTrackMonitorMapVo;
    shipName?: string;
    trackId: string;
    selectedCaptureId: string;
    children?: React.ReactNode;
    onSelectCapture: (id: string) => void;
}

/** 旧版 CaptureMap 的生命周期桥接；地图本体仍直接复用 ZhMap。 */
export function CaptureMap({
    records,
    map,
    shipName,
    trackId,
    selectedCaptureId,
    children,
    onSelectCapture
}: CaptureMapProps) {
    const [layerType, setLayerType] = useState<"vector" | "satellite">("vector");
    const [fallbackFullscreen, setFallbackFullscreen] = useState(false);
    const trajectory = map?.trajectoryRepresentation === "OMITTED" ? [] : (map?.trajectory ?? []);
    const position = map?.currentShipPosition ?? map?.lastKnownShipPosition;
    /** 旧页抓拍节点始终按时间正序编号，不受侧栏倒序展示影响。 */
    const captureSequence = new Map(
        [...records]
            .sort(
                (left, right) =>
                    Date.parse(String(left.displayTime || left.captureTime)) -
                    Date.parse(String(right.displayTime || right.captureTime))
            )
            .map((record, index) => [String(record.id), index + 1])
    );
    const captureMarkers = records
        .filter(record => Number.isFinite(record.longitude) && Number.isFinite(record.latitude))
        .map(record => ({
            id: String(record.id),
            coordinate: [record.longitude as number, record.latitude as number] as [number, number],
            text: String(captureSequence.get(String(record.id)) ?? 1),
            className: `capture-map__marker${String(record.id) === selectedCaptureId ? " is-active" : ""}`,
            variant: "capture" as const,
            color: getCaptureCategoryOption(record).color
        }));
    const markers = position
        ? [
              ...captureMarkers,
              {
                  id: "current-ship-position",
                  coordinate: [position.longitude, position.latitude] as [number, number],
                  text: shipName?.trim() || "未命名船舶",
                  className: "capture-map__ship-marker",
                  variant: "ship" as const,
                  color: "#04c900",
                  interactive: false
              }
          ]
        : captureMarkers;

    const toggleFullscreen = async () => {
        const element = document.querySelector<HTMLElement>(".capture-map");
        if (document.fullscreenElement) {
            await document.exitFullscreen();
            return;
        }
        try {
            await element?.requestFullscreen();
        } catch {
            setFallbackFullscreen(value => !value);
        }
    };

    return (
        <div
            className={`capture-map${fallbackFullscreen ? " is-fallback-fullscreen" : ""}`}
            data-map-layer-type={layerType}
            data-marker-count={captureMarkers.length}
        >
            <ZhihaoMap
                label="在途监管"
                layerType={layerType}
                center={position ? [position.longitude, position.latitude] : undefined}
                showCenterMarker={false}
                track={{ id: trackId, points: trajectory, color: "#3370ff" }}
                markers={markers}
                onMarkerClick={onSelectCapture}
            />
            <div className="capture-map__card-overlay">{children}</div>
            <div className="tool-panel-container">
                <button
                    type="button"
                    className={`switch-btn${layerType === "satellite" ? " active" : ""}`}
                    onClick={() => setLayerType(value => (value === "satellite" ? "vector" : "satellite"))}
                >
                    <i className="iconfont icon-space" />
                    <span className="button-text">卫星</span>
                </button>
            </div>
            <button
                type="button"
                className="capture-map__fullscreen-button"
                aria-label="全屏查看地图"
                onClick={() => void toggleFullscreen()}
            >
                <i className="iconfont icon-quanping" aria-hidden="true" />
            </button>
        </div>
    );
}
